import * as R from 'ramda'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { styled } from '@mui/material/styles'
import { Divider } from '@mui/material'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import {
  GroupSeen,
  IMessage,
  MessageSender,
  RawContactMessage,
  WithBodyMessage,
} from '@weblite/ts-action/message'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import hostConf from '$config/frontend'
// Logic

import { useRoom } from '$logic/room/react-hook'
import {
  useMessageByIid,
  useMessageDownloadStatus,
  useMessageShareStatus,
} from '$logic/message/react-hook'
// Components
import {
  RemoveIcon,
  EditOutlinedIcon,
  ReplyIcon,
  ForwardIcon,
  CheckCircleIcon,
  CopyIcon,
  LinkIcon,
  DownloadIcon,
  ReportIcon,
  ShareIcon,
  PinMessage,
  UnpinIcon,
  CallIcon,
  SeenIcon,
} from '$common/icons'
import {
  showPinMessageConfirmModal,
  showUnPinMessageConfirmModal,
} from '$comp/modals/confirmation-dialog'
import {
  canCopyMessage,
  canDeleteMessage,
  canEditMessage,
  canForwardMessage,
  canPinMessage,
  canSendMessage,
  DownloadableMessage,
  getPermissionOnWappMessage,
  isDownloadableMessage,
  isMessageFromClientUser,
  openForwardListHandler,
  handleDeleteMessagesModal,
} from '$logic/message'
import { copyToClipboard } from '$func/clipboard'
import { sendFieldApis } from '$comp/_app/components/chat/components/footer/components/send-field'
import {
  showLinkSavedInMemorySnackbar,
  showPhoneNumberSavedInMemorySnackbar,
  showReportSendSnackbar,
  showTextSavedInMemorySnackbar,
} from '$comp/snackbars'
import {
  roomSelectionAPI,
  useRoomPinsIsOpen,
} from '$comp/_app/components/chat/react-hooks'
import { useHallInfo } from '$logic/hall/react-hook'
import { useAsyncFunction } from '$func/async-function.react-hook'
import {
  scrollToMessageByIid,
  useOpenedRoomPermissions,
} from '$logic/opened-room'
import { handleRoomPinMessage } from '$logic/pin/service'
import { roomSearchApi } from '$comp/_app/components/chat/room'
import { AvatarGroup } from '$common/avatar-group'
import { t } from '$i18n'
import { getGroupSeen } from '$logic/room-seen'
import { showMessageSeenListDrawer } from '$comp/modals/message-seen-list'
import { getClientUserId } from '$logic/client-user'
import { weekInMilliSeconds } from '$func/time'
import { roomPendingEditByIidView } from '$logic/pending-edits/react-hook'
import { roomPendingEditUploadByIidView } from '$logic/pending-edit-uploads/react-hook'
import { isPhoneOrTablet } from '$func/environmentInfo'
import { closeRoomPins } from '$logic/router'
import { useClientUserHasPermissionToForwardMessage } from '$logic/subscription/react-hook'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { ContextMenuItem, ContextMenuList } from './components'
import { dateWithOffset } from '$setup/time-offset'

const REQ_SEEN_LIST_MEMBER_LIMIT = 100

const StyledDivider = styled(Divider)(({ theme }) => ({
  borderColor: theme.palette.veryLightGray,
  margin: '0 !important',
  '.MuiDivider-wrapper': {
    display: 'none',
  },
  ':before': {
    borderColor: theme.palette.veryLightGray,
  },
  ':after': {
    borderColor: theme.palette.veryLightGray,
  },
}))

interface Props {
  roomId: string
  iid: number
  xAxis: number
  yAxis: number
  isOpen: boolean
}

export function ContextMenu({ roomId, iid, xAxis, yAxis, isOpen }: Props) {
  const pinMode = useRoomPinsIsOpen()

  const { type: roomType, seenIid: roomSeenIid } = useRoom(roomId) || {}
  const { message } = useMessageByIid(iid, roomId)

  const messageBody = (message as WithBodyMessage)?.body
  const isPv = roomType === 'PV'
  const isHall = roomType === 'CHANNEL' || roomType === 'GROUP'
  const fromMe = !!message && isMessageFromClientUser(message)

  const hasPermissionToForwardMessage =
    useClientUserHasPermissionToForwardMessage(roomId)

  const { hall } = useHallInfo(isHall, roomId)

  const { value: canReply } = useAsyncFunction(
    pinMode ? () => Promise.resolve(false) : () => canSendMessage(roomId),
    [],
  )

  const { downloadable, download } = useMessageDownloadStatus(
    message as IMessage,
  )
  const { shareable, onShare: onShareClick } = useMessageShareStatus(
    message as DownloadableMessage,
  )

  const { openedRoomPermissions } = useOpenedRoomPermissions()
  const hasPinPermission = openedRoomPermissions?.pinMessage

  const isPinnableMessage = useMemo(
    () => (message ? canPinMessage(message) : false),
    [message],
  )

  const canPin = isPinnableMessage && hasPinPermission
  const { value: canEdit } = useAsyncFunction(
    async () => (message ? canEditMessage(message) : false),
    [message],
  )

  const { value: editWapp } = useAsyncFunction(
    () => getPermissionOnWappMessage(message),
    [message],
  )
  const { canEditWapp, onEditWapp, canDeleteWapp = false } = editWapp || {}

  const { value: canCopy } = useAsyncFunction(
    async () => (message ? canCopyMessage(message) : false),
    [message],
  )

  const { value: canDelete } = useAsyncFunction(
    async () => (message ? canDeleteMessage(message) : false),
    [message],
  )

  const canForward = useMemo(
    () =>
      message
        ? canForwardMessage(message) && hasPermissionToForwardMessage
        : false,
    [message, hasPermissionToForwardMessage],
  )

  const canReqGroupSeen = useMemo(() => {
    if (!message || !hall) return false
    return (
      roomType === 'GROUP' &&
      hall.membersCount < REQ_SEEN_LIST_MEMBER_LIMIT &&
      dateWithOffset() - +new Date(message.date) < weekInMilliSeconds &&
      getClientUserId() === (message.sender as MessageSender).id &&
      roomSeenIid &&
      iid <= roomSeenIid
    )
  }, [roomType, message, hall, iid, roomSeenIid])

  const { value: viewers, loading: seenLoading } = useAsyncFunction(
    async () =>
      canReqGroupSeen
        ? getGroupSeen(roomId, iid)
        : ([] as Pick<GroupSeen, 'userId' | 'seenAt'>[]),
    [roomId, iid, canReqGroupSeen],
  )

  const seenCount = viewers?.length

  const pendingEdit = useSelector(roomPendingEditByIidView(roomId, iid))
  const pendingEditUpload = useSelector(
    roomPendingEditUploadByIidView(roomId, iid),
  )
  const pendingMode = pendingEdit || pendingEditUpload

  const onCopy = async () => {
    if (message?.type === 'CONTACT') {
      copyToClipboard(message.attachment?.phone)
      showPhoneNumberSavedInMemorySnackbar()
    } else {
      copyToClipboard(messageBody)
      showTextSavedInMemorySnackbar()
    }
    await closeCurrentModal()
  }

  const onCall = async () => {
    await closeCurrentModal()
    window.open(`tel:${(message as RawContactMessage).attachment?.phone}`)
  }

  const onForward = async () => {
    await closeCurrentModal()
    await openForwardListHandler(roomId, [iid], 'CONTEXT_MENU')
  }

  const copyLink = async () => {
    copyToClipboard(`${hostConf.FRONT_HOST}/${hall?.identifier}/${iid}`)
    await closeCurrentModal()
    showLinkSavedInMemorySnackbar()
  }

  const onDownload = async () => {
    download?.()
    await closeCurrentModal()
  }

  const onShare = async () => {
    onShareClick?.()
    await closeCurrentModal()
  }

  const onReport = async () => {
    await closeCurrentModal()
    showReportSendSnackbar()
    //  TODO: Implement me
  }

  const onSelect = async () => {
    await closeCurrentModal()
    roomSelectionAPI.setSelection(iid)
  }

  const onPin = async () => {
    if (!message) return
    await closeCurrentModal()

    showPinMessageConfirmModal(roomId, (bidirectional?: boolean) => {
      analite('CHAT_MESSAGE_PIN', { type: message.type })
      handleRoomPinMessage(roomId, message.iid, true, !!bidirectional)
    })
  }
  const onUnPin = async () => {
    if (!message) return
    await closeCurrentModal()
    showUnPinMessageConfirmModal(() =>
      handleRoomPinMessage(roomId, message.iid, false),
    )
  }
  const onEdit = async () => {
    await closeCurrentModal()
    if (pinMode && message && !canEditWapp) {
      await closeRoomPins()
      await scrollToMessageByIid(message.iid)
    }
    roomSearchApi.exitSearch()

    if (canEditWapp) onEditWapp?.()
    else sendFieldApis.setEditContent(message as WithBodyMessage)
  }

  const onReply = async () => {
    await closeCurrentModal()
    roomSearchApi.exitSearch()
    sendFieldApis.setReplyContent(iid)
  }

  const onRemove = async () => {
    if (!message) return
    await closeCurrentModal()
    handleDeleteMessagesModal([message], canDeleteWapp, roomId)
  }

  return (
    <ContextMenuList
      open={isOpen}
      xAxis={xAxis}
      yAxis={yAxis}
      onClose={closeCurrentModal}
    >
      {canReply && !pinMode ? (
        <ContextMenuItem text={t('contextMenu.reply')} onClick={onReply}>
          <ReplyIcon />
        </ContextMenuItem>
      ) : undefined}

      {!pendingMode && canForward ? (
        <ContextMenuItem text={t('contextMenu.forward')} onClick={onForward}>
          <ForwardIcon sx={{ color: 'secondary.main' }} />
        </ContextMenuItem>
      ) : undefined}

      {canCopy ? (
        <ContextMenuItem text={t('menu.common.copy')} onClick={onCopy}>
          <CopyIcon />
        </ContextMenuItem>
      ) : undefined}

      {message?.type === 'CONTACT' ? (
        <ContextMenuItem text={t('menu.phone.call')} onClick={onCall}>
          <CallIcon />
        </ContextMenuItem>
      ) : undefined}

      {isHall && hall?.public ? (
        <ContextMenuItem text={t('contextMenu.copyLink')} onClick={copyLink}>
          <LinkIcon />
        </ContextMenuItem>
      ) : undefined}

      {message && isDownloadableMessage(message.type) && downloadable ? (
        <ContextMenuItem
          text={t('contextMenu.saveToDownloads')}
          onClick={onDownload}
        >
          <DownloadIcon />
        </ContextMenuItem>
      ) : undefined}

      {downloadable && shareable ? (
        <ContextMenuItem text={t('menu.common.share')} onClick={onShare}>
          <ShareIcon />
        </ContextMenuItem>
      ) : undefined}

      {!fromMe && !isPv ? (
        <ContextMenuItem text={t('menu.common.report')} onClick={onReport}>
          <ReportIcon />
        </ContextMenuItem>
      ) : undefined}

      {!isPhoneOrTablet ? (
        <ContextMenuItem text={t('menu.common.select')} onClick={onSelect}>
          <CheckCircleIcon />
        </ContextMenuItem>
      ) : undefined}

      {!!seenCount || canPin || canEdit || canEditWapp || canDelete ? (
        <StyledDivider />
      ) : undefined}

      {canReqGroupSeen ? (
        <ContextMenuItem
          text={t('contextMenu.haveSeen')(seenCount)}
          onClick={async () => {
            await closeCurrentModal()
            showMessageSeenListDrawer(viewers!)
          }}
          loading={seenLoading}
          secondaryChildren={
            <AvatarGroup
              userIds={seenLoading ? [] : R.pluck('userId', viewers!)}
              sx={{ marginLeft: '11px' }}
              size={AvatarSize._4XS}
            />
          }
        >
          <SeenIcon
            sx={{ viewBox: '0 0 24 24', width: '24px', height: '24px' }}
          />
        </ContextMenuItem>
      ) : undefined}

      {canPin ? (
        message?.pinned ? (
          <ContextMenuItem text={t('chatList.menu.unPin')} onClick={onUnPin}>
            <UnpinIcon />
          </ContextMenuItem>
        ) : (
          <ContextMenuItem text={t('chatList.menu.pin')} onClick={onPin}>
            <PinMessage />
          </ContextMenuItem>
        )
      ) : undefined}

      {!pendingMode && (canEdit || canEditWapp) ? (
        <ContextMenuItem text={t('common.edit')} onClick={onEdit}>
          <EditOutlinedIcon sx={{ color: 'secondary.main' }} />
        </ContextMenuItem>
      ) : undefined}

      {canDelete ? (
        <ContextMenuItem text={t('common.remove')} onClick={onRemove}>
          <RemoveIcon />
        </ContextMenuItem>
      ) : undefined}
    </ContextMenuList>
  )
}
